import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  type FieldType,
  Skeleton,
  ValueType,
} from '@folio/components';
import {
  buildFieldSchema,
  InformationField,
  InformationFieldCell,
  InformationFieldLabel,
  InformationFieldWrapper,
  InlineInput,
} from '@folio/components/module-elements/InformationCard';
import { useAssertedParams } from '@folio/lib';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  keyValueFieldsToObject,
  type UpdateBrokerageFirmInputBody,
  useBrokerageFirmControllerUpdateBrokerageFirm,
} from 'korint-sdk';
import type { CountryCode } from 'libphonenumber-js/max';
import _ from 'lodash';
import { SquarePenIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Center, Group, Stack } from '@folio/components/ui/flex';

type SingleFieldConfig = {
  kind?: 'single';
  label?: string;
  accessorKey: string;
  type: FieldType;
  key: string;
  allowEdition?: boolean;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  countries?: CountryCode[];
  options?: string[];
  /**
   * Maps the edited value to a PATCH body. Defaults to writing as a customField
   * keyed by `key`. Override when the field targets a structured property
   * (e.g. `companyInfo.location.address`) that the backend deep-merges.
   */
  formDataAccessor?: (value: ValueType) => UpdateBrokerageFirmInputBody;
};

type GroupSubFieldConfig = {
  key: string;
  label?: string;
  accessorKey: string;
  type: FieldType;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  countries?: CountryCode[];
};

type GroupFieldConfig = {
  kind: 'group';
  key: string;
  label?: string;
  fields: ReadonlyArray<GroupSubFieldConfig>;
  /**
   * Maps the edited values to a single composite PATCH body. Required for
   * groups because the backend validates the composite (e.g. address line +
   * postCode + city together) and per-field PATCHes would fail.
   */
  formDataAccessor: (values: Record<string, ValueType | undefined>) => UpdateBrokerageFirmInputBody;
};

export type IntermediaryInformationCardModuleField = SingleFieldConfig | GroupFieldConfig;

type IntermediaryInformationCardModuleProps = {
  /**
   * Discriminant key resolved against `intermediaryInformationCard.titles.<titleKey>`.
   */
  titleKey: string;

  /**
   * The fields of the module.
   */
  fields: Array<IntermediaryInformationCardModuleField>;

  /**
   * The firm to read and edit. Defaults to the `brokerageFirmId` route param, which pages
   * reached without one (a broker's own firm) do not have.
   */
  brokerageFirmId?: string;
};

function GroupedInformationField({
  field,
  brokerageFirm,
  brokerageFirmId,
  onSaved,
}: {
  field: GroupFieldConfig;
  brokerageFirm: Record<string, unknown>;
  brokerageFirmId: string;
  onSaved: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const initialValues = useMemo(
    () =>
      Object.fromEntries(
        field.fields.map(sub => [
          sub.key,
          _.get(brokerageFirm, sub.accessorKey) as ValueType | undefined,
        ]),
      ),
    [brokerageFirm, field.fields],
  );

  const summary = useMemo(
    () =>
      field.fields
        .map(sub => initialValues[sub.key])
        .filter((v): v is ValueType => v !== undefined && v !== '')
        .join(', '),
    [field.fields, initialValues],
  );

  const [values, setValues] = useState<Record<string, ValueType | undefined>>(initialValues);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const subSchemas = useMemo(
    () =>
      new Map(
        field.fields.map(sub => [
          sub.key,
          buildFieldSchema(sub.type, {
            required: sub.required,
            minLength: sub.minLength,
            maxLength: sub.maxLength,
            countries: sub.countries,
          }),
        ]),
      ),
    [field.fields],
  );

  const { mutateAsync: updateBrokerageFirm } = useBrokerageFirmControllerUpdateBrokerageFirm();

  const {
    mutate: save,
    isPending,
    error: saveError,
    reset: resetSaveError,
  } = useMutation({
    mutationFn: async () => {
      await updateBrokerageFirm({
        brokerageFirmId,
        data: field.formDataAccessor(values),
      });
      await onSaved();
    },
    onSuccess: () => setIsOpen(false),
  });

  const handleSave = () => {
    const nextErrors: Record<string, string> = {};
    for (const sub of field.fields) {
      const result = subSchemas.get(sub.key)?.safeParse(values[sub.key]);
      if (result && !result.success) {
        nextErrors[sub.key] = result.error.issues[0]?.message ?? '';
      }
    }
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      save();
    }
  };

  const saveErrorMessage =
    saveError && typeof saveError === 'object' && 'userHelp' in saveError
      ? (saveError as { userHelp: string }).userHelp
      : saveError?.message;

  return (
    <>
      <InformationFieldWrapper>
        <InformationFieldLabel>
          {field.label ?? t(`intermediaryInformationCard.fields.${field.key}`)}
        </InformationFieldLabel>
        <InformationFieldCell>
          <Group justify="between" gap={2}>
            <div className="min-w-0 overflow-hidden text-ellipsis">{summary || '—'}</div>
            <Button
              name="edit-grouped-field"
              variant="ghost"
              size="sm"
              onClick={() => {
                setValues(initialValues);
                setValidationErrors({});
                resetSaveError();
                setIsOpen(true);
              }}
            >
              <SquarePenIcon className="w-4 h-4" />
            </Button>
          </Group>
        </InformationFieldCell>
      </InformationFieldWrapper>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {field.label ?? t(`intermediaryInformationCard.fields.${field.key}`)}
            </DialogTitle>
          </DialogHeader>
          <Stack gap={3}>
            {field.fields.map(sub => {
              const fieldError = validationErrors[sub.key];
              return (
                <Stack key={sub.key} gap={1}>
                  <label className="text-sm font-medium" htmlFor={`group-field-${sub.key}`}>
                    {sub.label ?? t(`intermediaryInformationCard.fields.${sub.key}`)}
                  </label>
                  <InlineInput
                    type={sub.type}
                    fieldKey={sub.key}
                    value={values[sub.key]}
                    onChange={next => {
                      if (saveError) resetSaveError();
                      if (fieldError) {
                        setValidationErrors(prev => {
                          const { [sub.key]: _removed, ...rest } = prev;
                          return rest;
                        });
                      }
                      setValues(prev => ({ ...prev, [sub.key]: next }));
                    }}
                  />
                  {fieldError && (
                    <div className="text-sm font-medium text-destructive">{fieldError}</div>
                  )}
                </Stack>
              );
            })}
            {saveErrorMessage && (
              <div className="text-sm font-medium text-destructive whitespace-pre-line">
                {saveErrorMessage}
              </div>
            )}
          </Stack>
          <DialogFooter>
            <Button
              name="cancel-grouped-edit"
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={isPending}
            >
              {t('intermediaryInformationCard.actions.cancel')}
            </Button>
            <Button name="save-grouped-edit" onClick={handleSave} loading={isPending}>
              {t('intermediaryInformationCard.actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function IntermediaryInformationCardModule({
  titleKey,
  fields,
  brokerageFirmId: brokerageFirmIdOverride,
}: IntermediaryInformationCardModuleProps) {
  const { t } = useTranslation();
  const { brokerageFirmId: brokerageFirmIdFromRoute } = useAssertedParams();
  const brokerageFirmId = brokerageFirmIdOverride ?? brokerageFirmIdFromRoute;

  const {
    data: brokerageFirm,
    status: getBrokerageFirmStatus,
    refetch: refetchBrokerageFirm,
  } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(brokerageFirmId),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const { mutateAsync: updateBrokerageFirm } = useBrokerageFirmControllerUpdateBrokerageFirm();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(`intermediaryInformationCard.titles.${titleKey}`)}</CardTitle>
      </CardHeader>
      <CardContent>
        {getBrokerageFirmStatus === 'pending' ? (
          fields.map(field => <Skeleton key={`${field.key}-skeleton`} className="h-10 w-full" />)
        ) : getBrokerageFirmStatus === 'error' ? (
          <Center className="h-full">{t('intermediaryInformationCard.fetchError')}</Center>
        ) : (
          <table className="w-full table-fixed">
            <tbody>
              {fields.map(field => {
                if (field.kind === 'group') {
                  return (
                    <GroupedInformationField
                      key={field.key}
                      field={field}
                      brokerageFirm={brokerageFirm}
                      brokerageFirmId={brokerageFirmId}
                      onSaved={refetchBrokerageFirm}
                    />
                  );
                }
                const {
                  label,
                  accessorKey,
                  key,
                  allowEdition,
                  type,
                  required,
                  minLength,
                  maxLength,
                  countries,
                  options,
                  formDataAccessor,
                } = field;
                return (
                  <InformationField
                    key={key}
                    fieldKey={key}
                    label={label ?? t(`intermediaryInformationCard.fields.${key}`)}
                    updateEntity={async (fieldKey: string, value: ValueType) => {
                      const data = formDataAccessor
                        ? formDataAccessor(value)
                        : { customFields: [{ key: fieldKey, value }] };
                      await updateBrokerageFirm({
                        brokerageFirmId,
                        data,
                      });
                      await refetchBrokerageFirm();
                    }}
                    editionMode={allowEdition ? 'INLINE' : 'DISABLED'}
                    type={type}
                    options={options}
                    value={_.get(brokerageFirm, accessorKey)}
                    validationRules={{ required, minLength, maxLength, countries }}
                  />
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}
